import React, { useState, useCallback, useMemo, memo, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import styled from "styled-components";
import { Notify, Utils } from "../../../utils";
import CustomLoader from "../../../components/CustomLoader";
import { Loader } from "../../../components";
import { LoaderContainer } from "../../../CommonStyles";
import { ExpenseService } from "../../../_service/ExpenseService";
import TextEditor from "../../../components/editor";

// Move styled components outside the component
const Divider = styled.hr`
  border: 1px solid #e0e0e0;
  margin: 0.5rem 0;
`;

const Button = styled.button`
  font-size: 16px;
  border: 1px solid #28a745;
  background-color: #28a745;
  color: #ffffff;
  padding: 0.5rem 1.25rem;
  border-radius: 4px;
  cursor: pointer;
  text-align: center;
  font-weight: 500;
  transition: background-color 0.2s ease, color 0.2s ease,
    border-color 0.2s ease;
  margin: 0;
  will-change: background-color, color, border-color;

  &:hover {
    background-color: #fff;
    color: #28a745;
    border-color: #28a745;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.875rem;
  width: 100%;
  box-sizing: border-box;
  text-transform: none;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

// Define static styles outside component
const staticStyles = {
  drawerPaper: {
    width: "60%",
    padding: 2,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  mainContainer: {
    mt: 2,
    display: "flex",
    height: "calc(100vh - 150px)",
  },
  sidebar: {
    border: "1px solid #E0E0E0",
    borderRadius: "4px",
    backgroundColor: "#f3f3f3",
    padding: "1rem",
    mr: 2,
    width: "25%",
  },
  contentArea: {
    flex: 1,
    pl: 2,
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 2,
    mt: 2,
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  nameRow: {
    display: "flex",
    gap: 2,
  },
  nameField: {
    flex: 1,
  },
  labelStyle: {
    mb: 1,
    fontWeight: "normal",
    fontSize: "12px",
  },
  titleStyle: {
    mb: "0.25rem",
    fontWeight: "bold",
    color: "#283A44",
    fontSize: "1.25rem",
  },
};

// Define interfaces for step components
interface StepProps {
  formData: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    permission: string;
    message: string;
  };
  handleInputChange: (field: string, value: string) => void;
  handleCancel?: () => void;
  handleContinue?: () => void;
  handlePrevious?: () => void;
  handleSubmit?: () => void;
  isValid: boolean;
  noAccessUsers?: any[];
  showSelectUserDropdown?: boolean;
  user?: any;
  emailTemplate?: any;
}

const StepOne = memo<StepProps>(
  ({
    formData,
    handleInputChange,
    handleCancel,
    handleContinue,
    isValid,
    noAccessUsers,
    showSelectUserDropdown,
    emailTemplate,
  }) => (
    <Box>
      <Typography variant="h6" sx={staticStyles.titleStyle}>
        1. Enter User Details
      </Typography>

      {showSelectUserDropdown && (
        <Box sx={{ mb: 2, mt: 2 }}>
          <Typography variant="body2" sx={staticStyles.labelStyle}>
            Select User
          </Typography>
          <Select
            fullWidth
            value={formData?.id || ""}
            onChange={(e) => {
              const selectedUser = noAccessUsers?.find(
                (u) => u.id === parseInt(String(e.target.value))
              );
              if (selectedUser) {
                handleInputChange("id", String(selectedUser.id));
                handleInputChange(
                  "firstName",
                  selectedUser?.user?.first_name || ""
                );
                handleInputChange(
                  "lastName",
                  selectedUser?.user?.last_name || ""
                );
                
                handleInputChange("email", selectedUser?.user?.email || "");

                let isSelectedUserStaff = selectedUser?.user?.is_staff || false;

                handleInputChange("isStaff", isSelectedUserStaff)

                // handleInputChange("message", isSelectedUserStaff ? `<p>Hi, {{name}}<br>You are invited to access Expense Claim. Please click the "Start Now" button to continue.</p>` : `<p>Hi, {{name}}<br>You are invited to access Self Service and Expense Claim. Please click the "Start Now" button to continue.</p>`);
                handleInputChange("message", isSelectedUserStaff ? (emailTemplate?.expenseOnly || `<p>Hi, {{name}}<br>You are invited to access Expense Claim. Please click the "Start Now" button to continue.</p>`) : (emailTemplate?.expenseAndSelfService || `<p>Hi, {{name}}<br>You are invited to access Self Service and Expense Claim. Please click the "Start Now" button to continue.</p>`));
              }
            }}
            displayEmpty
            sx={{
              bgcolor: "white",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#ccc",
              },
            }}
          >
            <MenuItem value="" disabled>
              Select a user
            </MenuItem>
            {noAccessUsers?.map((userItem) => (
              <MenuItem key={userItem.id} value={userItem.id}>
                {userItem.user.name} ({userItem.user.email})
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}

      <Box sx={staticStyles.formContainer}>
        <Box sx={staticStyles.nameRow}>
          <Box sx={staticStyles.nameField}>
            <Typography variant="body2" sx={staticStyles.labelStyle}>
              First Name
            </Typography>
            <Input
              type="text"
              // placeholder="Enter first name"
              value={formData.firstName}
              onChange={(e) => handleInputChange("firstName", e.target.value)}
              disabled
            />
          </Box>
          <Box sx={staticStyles.nameField}>
            <Typography variant="body2" sx={staticStyles.labelStyle}>
              Last Name
            </Typography>
            <Input
              type="text"
              // placeholder="Enter last name"
              value={formData.lastName}
              onChange={(e) => handleInputChange("lastName", e.target.value)}
              disabled
            />
          </Box>
        </Box>
        <Box>
          <Typography variant="body2" sx={staticStyles.labelStyle}>
            Email
          </Typography>
          <Input
            type="email"
            // placeholder="Enter email address"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            disabled
          />
        </Box>
        <Box sx={staticStyles.buttonContainer}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleContinue} disabled={!isValid}>
            Continue to 2
          </Button>
        </Box>
      </Box>
    </Box>
  )
);

const StepTwo = memo<StepProps>(
  ({
    formData,
    handleInputChange,
    handlePrevious,
    handleContinue,
    isValid,
    user,
    handleSubmit,
  }) => (
    <Box>
      <Typography variant="h6" sx={staticStyles.titleStyle}>
       2. Set User Permission
      </Typography>

      <p
        style={{
          fontSize: "0.75rem",
          color: "#999999",
          marginBottom: "1rem",
          lineHeight: "1.5",
        }}
      >
        Permission can be edited in the Users page within Expense settings.
      </p>

      <p
        style={{
          fontSize: "1rem",
          color: "#283A44",
          marginBottom: "1rem",
          lineHeight: "1.5",
        }}
      >
        <strong>Expenses</strong> {`[Choose from Submitter, Approver or Admin role]`}
      </p>

      <p
        style={{
          fontSize: "0.875rem",
          color: "#283A44",
          marginBottom: "0.5rem",
          lineHeight: "1.5",
        }}
      >
        {"Choose a role (required)"}
      </p>
      <Box sx={staticStyles.formContainer}>
        <Box>
          {/* <Typography variant="body2" sx={{...staticStyles.labelStyle, fontSize: '1rem'}}>
            Permission Level
          </Typography> */}
          <Select
            fullWidth
            value={formData.permission}
            placeholder="Select permission level"
            onChange={(e) => handleInputChange("permission", e.target.value)}
            sx={{
              bgcolor: "white",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#ccc",
              },
            }}
          >
            <MenuItem value="" disabled>
              Choose a role
            </MenuItem>
            <MenuItem value="submitter">Submitter</MenuItem>
            <MenuItem value="approver">Approver</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </Select>
        </Box>

        {/* <p
          style={{
            fontSize: "0.75rem",
            color: "#999999",
            marginBottom: "0rem",
            lineHeight: "0.5",
          }}
        >
          Approvers can be approve and submit expense claims in Xero Expenses.
        </p>
        <p
          style={{
            fontSize: "0.75rem",
            color: "#28A745",
            borderBottom: "1px solid #28A745",
            width: "fit-content",
            marginBottom: "1rem",
            lineHeight: "0.5",
            paddingBottom: "0.5rem",
            paddingTop: "0.25rem",
            // cursor: "pointer",
          }}
          // onClick={() => window.open("https://xero.com", "_blank")}
        >
          Learn more about Xero Experiences.
        </p> */}

        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.875rem",
              color: "#283A44",
              mb: 2,
              fontWeight: "bold",
            }}
          >
            Role Descriptions:
          </Typography>
          
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box
              sx={{
                p: 2,
                backgroundColor: "#f8f9fa",
                border: "1px solid #e9ecef",
                borderRadius: "8px",
                borderLeft: "4px solid #28A745",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: "bold",
                  color: "#283A44",
                  mb: 0.5,
                }}
              >
                Submitter
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.75rem",
                  color: "#6c757d",
                  lineHeight: 1.5,
                }}
              >
                Can submit claims and view his own expenses.
              </Typography>
            </Box>

            <Box
              sx={{
                p: 2,
                backgroundColor: "#f8f9fa",
                border: "1px solid #e9ecef",
                borderRadius: "8px",
                borderLeft: "4px solid #28A745",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: "bold",
                  color: "#283A44",
                  mb: 0.5,
                }}
              >
                Approver
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.75rem",
                  color: "#6c757d",
                  lineHeight: 1.5,
                }}
              >
                The approver submits and approves others' expenses.
              </Typography>
            </Box>

            <Box
              sx={{
                p: 2,
                backgroundColor: "#f8f9fa",
                border: "1px solid #e9ecef",
                borderRadius: "8px",
                borderLeft: "4px solid #28A745",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: "bold",
                  color: "#283A44",
                  mb: 0.5,
                }}
              >
                Admin
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.75rem",
                  color: "#6c757d",
                  lineHeight: 1.5,
                }}
              >
                 Manage all Expenses settings.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={staticStyles.buttonContainer}>
          <Button onClick={handlePrevious}>Previous</Button>
          <Button
            onClick={user?.isEdit ? handleSubmit : handleContinue}
            disabled={!isValid}
          >
            {user?.isEdit ? "Save Changes" : "Continue to 3"}
          </Button>
        </Box>
      </Box>
    </Box>
  )
);

const StepThree = memo<StepProps>(
  ({ formData, handleInputChange, handlePrevious, handleSubmit, isValid }) => (
    <Box>
      <Typography variant="h6" sx={staticStyles.titleStyle}>
        3. Send Invitation Email
      </Typography>


      <Box sx={staticStyles.formContainer}>
        

        <Box sx={{ display: "flex", alignItems: "center", mb: 0 }}>
          <Typography sx={{ color: "#FF9800", mr: 0.5, fontSize: "0.875rem" }}>
            ⚠️
          </Typography>
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: "#FF9800",
              lineHeight: "1.5",
              fontStyle: "italic",
            }}
          >
            {"Important: Please keep {{name}} in the message. This is a template tag that will be automatically replaced with the user's name. Do not edit or remove it."}
          </Typography>
        </Box>

        <TextEditor
          handleChange={(val: any) => handleInputChange("message", val)}
          value={formData.message}
          placeholder="Enter Invitation Message"
        />

          <Typography variant="h6" sx={{ mt: 2, fontWeight: "bold" }}>
            Preview of the message body:
          </Typography>

        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: "4px",
            padding: "1rem",
            backgroundColor: "#f9f9f9",
            mb: 2,
          }}
        >
         
          <Box
            dangerouslySetInnerHTML={{
              __html: formData?.message?.replace(/{{name}}/g, formData?.firstName || "User")
            }}
            sx={{
              fontSize: "0.875rem",
              lineHeight: "1.5",
              "& p": { margin: 0 },
              "& br": { lineHeight: "1.5" },
              "& ul": { paddingLeft: "2rem", margin: "0.5rem 0" },
              "& ol": { paddingLeft: "2rem", margin: "0.5rem 0" },
              "& li": { marginBottom: "0.25rem" }
            }}
          />
        </Box>




        <Box sx={staticStyles.buttonContainer}>
          <Button onClick={handlePrevious}>Previous</Button>
          <Button onClick={handleSubmit} disabled={!isValid}>
            Submit
          </Button>
        </Box>
      </Box>
    </Box>
  )
);

interface InviteUserDrawerProps {
  open: boolean;
  onClose: () => void;
  refetch: () => void;
  user: any;
  noAccessUsers?: any[];
  showSelectUserDropdown?: boolean;
  emailTemplate?: any;
}

const InviteUserDrawer: React.FC<InviteUserDrawerProps> = ({
  open,
  onClose,
  user,
  refetch,
  noAccessUsers,
  showSelectUserDropdown,
  emailTemplate
}) => {
  const [activeStep, setActiveStep] = useState(1);
  const [formData, setFormData] = useState({
    id: user?.id,
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    permission: user?.roles?.[0] || "submitter",
    message: "",
    isStaff: user?.isStaff || false,
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Reset form data when user prop changes
    setFormData({
      id: user?.id || 0,
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
      permission: user?.roles?.[0] || "submitter",
      isStaff: user?.isStaff || false,
      message: user?.isStaff ? (emailTemplate?.expenseOnly || `<p>Hi, {{name}}<br>You are invited to access Expense Claim. Please click the "Start Now" button to continue.</p>`) : (emailTemplate?.expenseAndSelfService || `<p>Hi, {{name}}<br>You are invited to access Self Service and Expense Claim. Please click the "Start Now" button to continue.</p>`),
    });
  }, [user]);

  console.log("InviteUserDrawer user:", user);

  // Memoize input change handler with useCallback
  const handleInputChange = useCallback((field: string, value: string) => {
    console.log("Input changed:", field, value);
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Memoize step click handler
  const handleStepClick = useCallback((step: number) => {
    setActiveStep(step);
  }, []);

  // Memoize continue handler
  const handleContinue = useCallback(() => {
    if (activeStep < 3) {
      setActiveStep(activeStep + 1);
    }
  }, [activeStep]);

  // Memoize submit handler
  const handleSubmit = useCallback(async () => {
    setIsLoading(true);
    let payload = {
      employee_id: user?.id || formData.id,
      roles: [formData?.permission?.toLowerCase()],
      email_content: formData?.message,
      email: formData.email,
      template_code: user?.isStaff ? "INV-EXP-0001" : "INV-EXP-0002",
      email_subject: user?.isStaff ? (emailTemplate?.expenseOnlySubject || "You're invited to access Expense Claim") : (emailTemplate?.expenseAndSelfServiceSubject || "You're invited to access Self Service and Expense Claim"),  
    };
    console.log("Form submitted:", payload);

    try {
      const { status } = await ExpenseService._saveExpenseUser(payload);
      if (status >= 200 && status < 300) {
        Notify(
          user?.isEdit
            ? `Employee updated successfully!`
            : `Employee invited successfully!`,
          1
        );
        refetch();
        onClose();
        setIsLoading(false);
      }
    } catch (error: any) {
      Utils.showErrorMessage(error);
      onClose();
      setIsLoading(false);
    }

    onClose();
  }, [formData, onClose]);

  // Memoize cancel handler
  const handleCancel = useCallback(() => {
    onClose();
    setActiveStep(1);
    setFormData({
      id: 0,
      firstName: "",
      lastName: "",
      email: "",
      permission: "",
      message: "",
      isStaff: false,
    });
  }, [onClose]);

  // Memoize step 2 previous handler
  const handleStep2Previous = useCallback(() => {
    setActiveStep(1);
    // setFormData((prev) => ({
    //   ...prev,
    //   permission: "",
    // }));
  }, []);

  // Memoize step 3 previous handler
  const handleStep3Previous = useCallback(() => {
    setActiveStep(2);
    setFormData((prev) => ({
      ...prev,
      // message: "",
    }));
  }, []);

  // Memoize validation states
  const isStepOneValid = useMemo(
    () => Boolean((formData.firstName || formData.lastName) && formData.email),
    [formData.firstName, formData.lastName, formData.email]
  );

  const isStepTwoValid = useMemo(
    () => Boolean(formData.permission),
    [formData.permission]
  );

  const isStepThreeValid = useMemo(
    () => Boolean(formData.message && formData.email && formData.permission),
    [formData.message, formData.email, formData.permission]
  );

  // Memoize step checks
  const isStepOneActive = activeStep === 1;
  const isStepTwoActive = activeStep === 2;
  const isStepThreeActive = activeStep === 3;

  // Memoize sidebar steps
  const sidebarSteps = useMemo(
    () =>
      user?.isEdit
        ? [
            { step: 1, label: "1. Enter User Details" }, 
            { step: 2, label: "2. Set Permission" },
          ]
        : [
            { step: 1, label: "1. Enter User Details" },
            { step: 2, label: "2. Set Permission" },
            { step: 3, label: "3. Send Invitation Email" },
          ],
    [user]
  );

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email,
      isStaff: user?.isStaff || false,
    }));

    return () => {
      setActiveStep(1);
      setFormData({
        id: 0,
        firstName: "",
        lastName: "",
        email: "",
        permission: "Submitter",
        message: "",
        isStaff: false,
      });
    };
  }, [user]);

  console.log("InviteUserDrawer formData:", formData);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: staticStyles.drawerPaper,
      }}
    >
      <Box sx={staticStyles.header}>
        <Typography sx={{ fontSize: 18, fontWeight: "bold" }}>
          Invite a New User
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      <Box sx={staticStyles.mainContainer}>
        {/* Left Sidebar */}
        <Box sx={staticStyles.sidebar}>
          <Typography
            sx={{ fontSize: 14, fontWeight: "bold", mb: 1, color: "#7D7D7D" }}
          >
            Please complete the following steps to invite a user to Expenses:
          </Typography>
          {sidebarSteps.map(({ step, label }) => (
            <Box
              key={step}
              sx={{
                mb: 2,
                // cursor: "pointer",
                p: 1,
                borderRadius: 1,
                "&:hover": { backgroundColor: "#f0f8f0" },
                backgroundColor:
                  activeStep === step ? "#e8f5e9" : "transparent",
                borderLeft: activeStep === step ? "3px solid #28A745" : "none",
              }}
              // onClick={() => handleStepClick(step)}
            >
              <Typography
                sx={{
                  color: activeStep === step ? "#28A745" : "inherit",
                  fontSize: 14,
                  fontWeight: activeStep === step ? "bold" : "normal",
                }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Right Content Area */}
        {isLoading && (
          <LoaderContainer>
            <Loader />
          </LoaderContainer>
        )}
        <Box sx={staticStyles.contentArea}>
          {isStepOneActive && (
            <StepOne
              formData={formData}
              handleInputChange={handleInputChange}
              handleCancel={handleCancel}
              handleContinue={handleContinue}
              isValid={isStepOneValid}
              noAccessUsers={noAccessUsers || []}
              showSelectUserDropdown={showSelectUserDropdown}
              emailTemplate={emailTemplate}
            />
          )}

          {isStepTwoActive && (
            <StepTwo
              formData={formData}
              handleInputChange={handleInputChange}
              handlePrevious={handleStep2Previous}
              handleContinue={handleContinue}
              isValid={isStepTwoValid}
              user={user}
              handleSubmit={handleSubmit}
            />
          )}

          {isStepThreeActive && (
            <StepThree
              formData={formData}
              handleInputChange={handleInputChange}
              handlePrevious={handleStep3Previous}
              handleSubmit={handleSubmit}
              isValid={isStepThreeValid}
            />
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default InviteUserDrawer;
